import { createLogger } from 'shared/services/logger';
import { store } from 'shared/stores/core/decorators';
import { makeObservable } from 'shared/stores/core/make-observable';
import { unsetAll } from 'shared/util';
import { assert } from 'shared/util/assert';

const logger = createLogger('@store(IA_RESULTS)');

@store('IA_RESULTS')
export class ResultsStore {
  state = {
    stateByIaKey: {}
  };

  getters = {
    currentState ({get}) {
      const {imageId, macroId} = get('IA_STATE');
      return this.getters.getState({imageId, macroId});
    },
    getState: () => ({imageId, macroId}) => {
      if (!imageId || !macroId) {
        return null;
      }

      const syntheticKey = `${imageId}+${macroId}`;

      if (!this.state.stateByIaKey[syntheticKey]) {
        this.state.stateByIaKey[syntheticKey] = createState();
      }

      return this.state.stateByIaKey[syntheticKey];
    }
  };

  remap = {
    IA_STATE_CHANGE: 'IA_LOAD_RESULTS'
  };

  // Command to clear the current state
  IA_CLEAR_CURRENT_RESULTS () {
    const state = this.getters.currentState;
    if (state) {
      state.regionsData.splice(0);
      state.slideData.splice(0);
      unsetAll(state.metaData);
    }
  }

  // Command to request IA results from the database
  async IA_LOAD_RESULTS ({dispatch, get, resources}, options = {useCache: true}) {
    const {macroId, imageId} = get('IA_STATE');
    const stateRef = this.getters.currentState;
    const resultProvider = resources.get('IA_RESULTS');

    // If we've already got data, or if we're not using the cache, or if we're already loading data, there's
    // no need to do anything so just exit.
    /**
     * | isLoaded | useCache | isLoading | EXIT  |
     * |     T    |    T     |    T      |   T   | // Invalid State
     * |     T    |    T     |    F      |   T   |
     * |     T    |    F     |    T      |   F   |
     * |     F    |    F     |    F      |   F   |
     * |     F    |    T     |    T      |   T   |
     * |     F    |    F     |    F      |   F   |
     */
    if ((stateRef.isLoaded && options.useCache) || stateRef.isLoading) {
      return;
    }

    stateRef.isLoading = true;
    const result = await resultProvider.list(imageId, macroId);
    stateRef.isLoading = false;
    stateRef.isLoaded = true;

    if (stateRef.slideData.length > 0) {
      dispatch('IA_CLEAR_CURRENT_RESULTS');
    }

    if (result && result.data) {
      stateRef.slideData.push(...getSlideData(result.data));
      stateRef.regionsData.push(...getRegionsData(result));
      stateRef.metaData = result.metaData || {}; // TODO: Shouldn't this already be shaped?
      stateRef.isSlideDataValid = true;
    } else {
      // isSlideDataValid is supposed to indicate slide data contains data for deleted results. !result.data may
      // simply mean there's just no data; is this invalid? TODO: figure this out...
      stateRef.isSlideDataValid = false;
    }

    // TODO: This should trigger an event called IA_RESULTS_LOADED, which should be picked up in the IA JOBS store,
    //       where this logic belongs
    // Pending regions suggest there might be an outstanding job. Only trigger for the active image
    if (result && result.pendingRegions && result.pendingRegions.length > 0 && !hasStateChanged(get, imageId, macroId)) {
      dispatch('IA_CHECK_FOR_RUNNING_JOBS', {regions: result.pendingRegions});
    }
  }

  // Synchronize the results list with the annotations on the viewport
  IA_REGION_DELETED ({get, state}, {region}) {
    const annotations = get('ANNOTATIONS');
    const outputLayer = annotations.getLayer(region);

    const stateRef = this.getters.getState(outputLayer);
    const removedItem = stateRef.regionsData.find(d => d.region.id === region.id);

    if (removedItem) {
      stateRef.regionsData.splice(stateRef.regionsData.indexOf(removedItem), 1);
      stateRef.isSlideDataValid = false;
    }

    if (stateRef.regionsData.length === 0) {
      // Remove all items from the slideData array. Use splice so Vue can detect the change.
      stateRef.slideData.splice(0);
      stateRef.metaData = {};
    }
  }

  async IA_JOB_COMPLETE ({get, dispatch}, {imageId, macroId}) {
    assert.isRequired({imageId, macroId}, 'IA_JOB_COMPLETE');

    // If job wasn't for current state mark as dirty so it will refresh next time it's state is selected
    const stateRef = this.getters.getState({imageId, macroId});
    const iaState = get('IA_STATE');
    if (iaState.imageId !== imageId || iaState.macroId !== macroId) {
      stateRef.isLoaded = false;
    }

    // If the job's viewport is still active tell the annotations service to reload annotations.
    const hasViewport = get('VIEWPORT').hasViewport({imageId});
    if (hasViewport) {
      const iaOutputLayer = get('IA_ANNOTATIONS').getIaOutputLayer({imageId, macroId});
      const annotationsService = get('VIEWPORT').plugins('us:annotations:service', imageId);
      if (!iaOutputLayer || !annotationsService) {
        logger.warn('Cannot reinitialize output layers; missing image id or output layer or annotations service', imageId, iaOutputLayer, annotationsService);
        return;
      }
      annotationsService.layerReInit(imageId, iaOutputLayer.id);
    }

    if (!stateRef.isLoaded) {
      return;
    }

    // store "existing" regions before loading data because they get updated
    const existingRegions = [...stateRef.regionsData];

    await dispatch('IA_LOAD_RESULTS', {useCache: false});

    // If new regions were analyzed, show markup for the last region that was analyzed (if this job was kicked off
    // as a result of a 'delete" operation then regions will be empty). Set overwrite to false so that if the user
    // is currently looking at markup then it is not overwritten.
    const regions = stateRef.regionsData.filter(r => !existingRegions.find(e => e.displayId === r.displayId));
    if (regions.length > 0 && !hasStateChanged(get, imageId, macroId)) {
      dispatch('IA_SHOW_MARKUP', {region: regions[regions.length - 1], overwrite: false});
    }
  }

  // Command to delete current results from the database. Since results are stored in annotation layers, delete them.
  IA_DELETE_CURRENT_RESULTS ({dispatch}) {
    dispatch('IA_CLEAR_CURRENT_RESULTS');
    // This feels too imperative. It seems to me that we should clear results when output layers are deleted. TODO: rethink... too many things to rethink...
    dispatch('IA_DELETE_CURRENT_LAYERS');
  }

  // When layers are deleted, we need to reload results to ensure they're cleared.
  async IA_CURRENT_LAYERS_DELETED ({dispatch}) {
    await dispatch('IA_LOAD_RESULTS');
  }
}

function getSlideData (data) {
  return Object.keys(data).map(key => {
    if (data[key].hasOwnProperty('slide')) {
      return {
        output: key,
        slide: data[key].slide
      };
    }
  }).filter(Boolean);
}

function getRegionsData (result) {
  return Object.keys(result.regions).map((id) => getRegionInfo(result, Number(id))).filter(Boolean);
}

function getRegionInfo (result, id) {
  const data = result.data;
  return {
    displayId: result.regions[id].displayId, // Stop using this; it's not reliable
    id: id,
    region: result.regions[id],
    data: Object.keys(data).map(key => {
      if (data[key].hasOwnProperty(id)) {
        return {
          output: key,
          slide: data[key].slide,
          region: data[key][id]
        };
      }
    }).filter(Boolean)
  };
}

function hasStateChanged (get, imageId, macroId) {
  const iaState = get('IA_STATE');
  return iaState.imageId !== imageId || iaState.macroId !== macroId;
}

function createState () {
  return makeObservable({
    isLoading: false,
    isLoaded: false,
    slideData: [],
    isSlideDataValid: true, // Initial state with empty results is a valid state
    regionsData: [],
    metaData: {} // This comes from the results attributes in GetAnnotations2 (with things like colors)
  });
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/results/ResultsStore.js